// A SZERKESZTŐ-MÓD DRAFTJA: a látható csempék rendezett listája (id + méret + opció), tiszta reducerrel.
// A draft CSAK a mentésig él a komponensben; a Mégse eldobja. A mentés a `useDashboardLayout`-on át megy.
import { dashboardTileDef, type DashboardTileEntry, type DashboardTileSize } from '@atlas/shared';
import { useCallback, useMemo, useReducer } from 'react';

export type EditorAction =
  | { type: 'reset'; tiles: DashboardTileEntry[] }
  | { type: 'move'; from: number; to: number }
  | { type: 'remove'; id: string }
  | { type: 'add'; id: string }
  | { type: 'resize'; id: string; size: DashboardTileSize };

export interface EditorState {
  /** A kiinduló lista (a Mégse / „változott-e" ehhez mér). */
  initial: DashboardTileEntry[];
  tiles: DashboardTileEntry[];
}

function sameTiles(a: readonly DashboardTileEntry[], b: readonly DashboardTileEntry[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (x, i) =>
        x.id === b[i]?.id && x.size === b[i].size && x.opts?.windowDays === b[i].opts?.windowDays,
    )
  );
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'reset':
      return { initial: action.tiles, tiles: action.tiles };
    case 'move': {
      const { from, to } = action;
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= state.tiles.length ||
        to >= state.tiles.length
      )
        return state;
      const next = [...state.tiles];
      const [moved] = next.splice(from, 1);
      if (moved === undefined) return state;
      next.splice(to, 0, moved);
      return { ...state, tiles: next };
    }
    case 'remove':
      // a KÖTELEZŐ csempe nem vehető le (a katalógus `removable: false`)
      if (dashboardTileDef(action.id)?.removable === false) return state;
      return { ...state, tiles: state.tiles.filter((t) => t.id !== action.id) };
    case 'add': {
      const def = dashboardTileDef(action.id);
      if (!def || state.tiles.some((t) => t.id === action.id)) return state;
      return { ...state, tiles: [...state.tiles, { id: def.id, size: def.defaultSize }] };
    }
    case 'resize': {
      const def = dashboardTileDef(action.id);
      if (!def?.allowedSizes.includes(action.size)) return state;
      return {
        ...state,
        tiles: state.tiles.map((t) => (t.id === action.id ? { ...t, size: action.size } : t)),
      };
    }
    default:
      return state;
  }
}

export interface DashboardEditorHandle {
  tiles: DashboardTileEntry[];
  dirty: boolean;
  dispatch: (action: EditorAction) => void;
  move: (from: number, to: number) => void;
  remove: (id: string) => void;
  add: (id: string) => void;
  resize: (id: string, size: DashboardTileSize) => void;
  /** Új kiinduló lista (szerkesztés indulásakor / elvetéskor). */
  restart: (tiles: DashboardTileEntry[]) => void;
}

export function useDashboardEditor(initial: DashboardTileEntry[]): DashboardEditorHandle {
  const [state, dispatch] = useReducer(editorReducer, { initial, tiles: initial });
  const dirty = useMemo(() => !sameTiles(state.initial, state.tiles), [state]);
  return {
    tiles: state.tiles,
    dirty,
    dispatch,
    move: useCallback((from, to) => dispatch({ type: 'move', from, to }), []),
    remove: useCallback((id) => dispatch({ type: 'remove', id }), []),
    add: useCallback((id) => dispatch({ type: 'add', id }), []),
    resize: useCallback((id, size) => dispatch({ type: 'resize', id, size }), []),
    restart: useCallback((tiles) => dispatch({ type: 'reset', tiles }), []),
  };
}
